import * as React from "react";
import {useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useRecoilValue} from "recoil";
import {
   Alert,
   Box,
   Button,
   Checkbox,
   Chip,
   CircularProgress,
   LinearProgress,
   Stack,
   Table,
   TableBody,
   TableCell,
   TableHead,
   TableRow,
   Tooltip
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Page from "../../../layout/Page";
import PageHeader from "../../../layout/PageHeader";
import PageContent from "../../../layout/PageContent";
import {BodyText, Link} from "../../../layout/Typography";
import {Dialogs} from "../../../DialogProvider";
import {SavingUI} from "../../../SavingProvider";
import {CurrentUserIsAdmin} from "../../../atoms/CurrentUser";
import {BrandItemPagePath} from "../../brand/brandItem/BrandItemPage";
import {
   BrandItemMasterDataMigrationItemStatus as ItemStatus,
   BrandItemMasterDataMigrationRunKind as RunKind,
   BrandItemMasterDataMigrationRunStatus as RunStatus,
   BrandItemMasterDataMigrationSignalResult as SignalResult,
   MasMigrationItemsQuery,
   useMasMigrationItemsQuery,
   useMasMigrationMigrateMutation,
   useMasMigrationRePreviewMutation,
   useMasMigrationRunQuery
} from "../../../Queries";

export const MasMigrationRunPagePath = (runId: string) => "/admin/mas-migration/run/" + runId;

const POLL_MS = 1800;

type MigrationItem = NonNullable<NonNullable<MasMigrationItemsQuery['BrandItemMasterDataMigrationItems']>[number]>;
type FieldSnapshot = NonNullable<MigrationItem['current']>;
type Signal = NonNullable<NonNullable<MigrationItem['signals']>[number]>;

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

/** Side-by-side comparison fields, in the order shown in each row. */
const COMPARE_FIELDS: { key: keyof FieldSnapshot; label: string }[] = [
   {key: 'name', label: 'Name'},
   {key: 'style', label: 'Style'},
   {key: 'vendor', label: 'Vendor'},
   {key: 'type', label: 'Type'},
   {key: 'color', label: 'Color'},
   {key: 'size', label: 'Size'},
   {key: 'ean', label: 'EAN'},
   {key: 'sku', label: 'SKU'},
   {key: 'externalId', label: 'Ext. ID'},
];

const STATUS_META: Record<ItemStatus, { color: ChipColor; label: string }> = {
   [ItemStatus.Queued]: {color: 'default', label: 'Queued'},
   [ItemStatus.Processing]: {color: 'info', label: 'Processing'},
   [ItemStatus.Auto]: {color: 'success', label: 'Auto'},
   [ItemStatus.Review]: {color: 'warning', label: 'Review'},
   [ItemStatus.NoMatch]: {color: 'default', label: 'No match'},
   [ItemStatus.AlreadyLinked]: {color: 'default', label: 'Already linked'},
   [ItemStatus.Migrated]: {color: 'success', label: 'Migrated'},
   [ItemStatus.Stale]: {color: 'warning', label: 'Stale'},
   [ItemStatus.Failed]: {color: 'error', label: 'Failed'},
};

const SIGNAL_RESULT_COLOR: Record<SignalResult, ChipColor> = {
   [SignalResult.Match]: 'success',
   [SignalResult.WeakMatch]: 'info',
   [SignalResult.Contradiction]: 'error',
   [SignalResult.Ambiguous]: 'warning',
   [SignalResult.DiscardedShared]: 'default',
   [SignalResult.Missing]: 'default',
};

const SIGNAL_TYPE_LABEL: Record<string, string> = {
   Sku: 'SKU',
   Ean: 'EAN',
   ExternalId: 'Ext.ID',
   ColorSize: 'Color/Size',
};

const normalize = (value?: string | null) => (value || '').trim().toLowerCase();

const statusChip = (item: MigrationItem) => {
   const status = item.status;
   if (!status) {
      return null;
   }
   if (status === ItemStatus.Queued || status === ItemStatus.Processing) {
      return <Chip size="small" icon={<CircularProgress size={12}/>} label={STATUS_META[status].label}/>;
   }
   // A migrated item that still carries an error was linked but its MAS refresh failed.
   if (status === ItemStatus.Migrated && item.error) {
      return <Tooltip title="Linked, but the post-link MAS refresh failed — re-run a MAS refresh for this item.">
         <Chip size="small" color="warning" label="Migrated *"/>
      </Tooltip>;
   }
   const meta = STATUS_META[status];
   return <Chip size="small" color={meta.color} label={meta.label}/>;
};

const SignalChips: React.FC<{ signals?: Signal[] | null }> = ({signals}) => {
   if (!signals || signals.length === 0) {
      return <BodyText type="body2" sx={{color: 'text.secondary'}}>—</BodyText>;
   }
   return <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.5}>
      {signals.map((signal, index) => {
         if (!signal?.type || !signal.result) {
            return null;
         }
         const typeLabel = SIGNAL_TYPE_LABEL[signal.type] || signal.type;
         const weight = signal.weight ? signal.weight.toLowerCase() : '';
         const label = `${typeLabel} · ${signal.result}${weight ? ` · ${weight}` : ''}`;
         const tooltip = [signal.detail, signal.score != null ? `score ${signal.score}` : null]
            .filter(Boolean).join(' — ');
         const chip = <Chip key={index} size="small" variant="outlined"
                            color={SIGNAL_RESULT_COLOR[signal.result]} label={label}/>;
         return tooltip ? <Tooltip key={index} title={tooltip}>{chip}</Tooltip> : chip;
      })}
   </Stack>;
};

/** Renders current vs MAS-candidate values per field, flagging mismatches in red. */
const FieldComparison: React.FC<{ item: MigrationItem }> = ({item}) => {
   const rows = COMPARE_FIELDS
      .map(field => ({
         field,
         current: item.current?.[field.key] || '',
         candidate: item.candidate?.[field.key] || ''
      }))
      .filter(row => row.current || row.candidate);

   if (rows.length === 0) {
      return <BodyText type="body2" sx={{color: 'text.secondary'}}>No snapshot</BodyText>;
   }

   return <Box sx={{
      display: 'grid',
      gridTemplateColumns: '64px 1fr 1fr',
      columnGap: 1,
      rowGap: 0.25,
      fontSize: '12px',
      minWidth: 360
   }}>
      {rows.map(({field, current, candidate}) => {
         const mismatch = !!current && !!candidate && normalize(current) !== normalize(candidate);
         return <React.Fragment key={field.key}>
            <Box sx={{color: 'text.secondary'}}>{field.label}</Box>
            <Box sx={{wordBreak: 'break-word'}}>{current || <span style={{color: '#bbb'}}>—</span>}</Box>
            <Box sx={{
               wordBreak: 'break-word',
               color: mismatch ? 'error.main' : 'success.main',
               fontWeight: mismatch ? 600 : 400
            }}>{candidate || <span style={{color: '#bbb'}}>—</span>}</Box>
         </React.Fragment>;
      })}
   </Box>;
};

const RunSummary: React.FC<{
   counters: { label: string; value: number; color: ChipColor }[];
   processedCount: number;
   requestedCount: number;
   active: boolean;
}> = ({counters, processedCount, requestedCount, active}) => {
   const pct = requestedCount > 0 ? Math.round((processedCount / requestedCount) * 100) : 0;
   return <Box sx={{mb: 2}}>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.5}}>
         <BodyText type="body2">{processedCount} / {requestedCount} processed</BodyText>
         {active && <CircularProgress size={14}/>}
      </Box>
      <LinearProgress variant="determinate" value={pct} sx={{height: 8, borderRadius: 4, mb: 1}}/>
      <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.5}>
         {counters.map(counter => (
            <Chip key={counter.label} size="small" color={counter.value > 0 ? counter.color : 'default'}
                  variant={counter.value > 0 ? 'filled' : 'outlined'}
                  label={`${counter.label}: ${counter.value}`}/>
         ))}
      </Stack>
   </Box>;
};

export const MasMigrationRunPage = () => {
   const {runId} = useParams<{ runId: string }>();
   const navigate = useNavigate();
   const isAdministrator = useRecoilValue(CurrentUserIsAdmin);

   const [migrate] = useMasMigrationMigrateMutation();
   const [rePreview] = useMasMigrationRePreviewMutation();

   const runQuery = useMasMigrationRunQuery({
      variables: {id: runId},
      skip: !runId,
      fetchPolicy: "no-cache"
   });
   const itemsQuery = useMasMigrationItemsQuery({
      variables: {runId: runId},
      skip: !runId,
      fetchPolicy: "no-cache"
   });

   const run = runQuery.data?.BrandItemMasterDataMigrationRun;
   const items = useMemo(
      () => (itemsQuery.data?.BrandItemMasterDataMigrationItems || []).filter((item): item is MigrationItem => !!item),
      [itemsQuery.data]
   );

   const isPreview = run?.type === RunKind.Preview;
   const terminal = run?.status === RunStatus.Completed || run?.status === RunStatus.Failed;

   // Poll both queries while the run is in flight; stop once it reaches a terminal state.
   // Re-applied on runId change so navigating to a freshly-created run resumes polling.
   useEffect(() => {
      if (!runId) {
         return;
      }
      if (terminal) {
         runQuery.stopPolling();
         itemsQuery.stopPolling();
         return;
      }
      runQuery.startPolling(POLL_MS);
      itemsQuery.startPolling(POLL_MS);
      return () => {
         runQuery.stopPolling();
         itemsQuery.stopPolling();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [runId, terminal]);

   // Pre-select every Auto row as it streams in, while preserving manual toggles.
   const [selected, setSelected] = useState<Set<string>>(new Set());
   const seenAutoRef = useRef<Set<string>>(new Set());

   // Start each run with a clean selection so a newly opened run isn't seeded from a previous one.
   useEffect(() => {
      setSelected(new Set());
      seenAutoRef.current = new Set();
   }, [runId]);

   useEffect(() => {
      if (!isPreview) {
         return;
      }
      const freshAuto = items.filter(item =>
         item.status === ItemStatus.Auto && item.proposedMasterDataVariantId && !seenAutoRef.current.has(item._id));
      if (freshAuto.length === 0) {
         return;
      }
      freshAuto.forEach(item => seenAutoRef.current.add(item._id));
      setSelected(prev => {
         const next = new Set(prev);
         freshAuto.forEach(item => next.add(item._id));
         return next;
      });
   }, [items, isPreview]);

   if (!isAdministrator) {
      return <Page>
         <PageHeader title="MAS migration"/>
         <PageContent>
            <Alert severity="error">This tool is available to administrators only.</Alert>
         </PageContent>
      </Page>;
   }

   const selectableAutoIds = items
      .filter(item => item.status === ItemStatus.Auto && item.proposedMasterDataVariantId)
      .map(item => item._id);
   const selectedCount = selectableAutoIds.filter(id => selected.has(id)).length;
   const allAutoSelected = selectableAutoIds.length > 0 && selectedCount === selectableAutoIds.length;

   const toggleRow = (id: string) => setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
         next.delete(id);
      } else {
         next.add(id);
      }
      return next;
   });

   const toggleAll = () => setSelected(prev => {
      if (allAutoSelected) {
         const next = new Set(prev);
         selectableAutoIds.forEach(id => next.delete(id));
         return next;
      }
      return new Set([...Array.from(prev), ...selectableAutoIds]);
   });

   const openBrandItem = (item: MigrationItem) => {
      if (!item.brandHouseId) {
         return;
      }
      window.open(BrandItemPagePath(item.brandHouseId, item.brandItemId, false), '_blank');
   };

   const onMigrate = () => {
      const selections = items
         .filter(item => selected.has(item._id) && item.status === ItemStatus.Auto && item.proposedMasterDataVariantId)
         .map(item => ({brandItemId: item.brandItemId, masterDataVariantId: item.proposedMasterDataVariantId!}));
      if (selections.length === 0) {
         Dialogs.info({
            title: 'Nothing selected',
            subtitle: 'Select at least one auto-matched item to migrate.'
         });
         return;
      }
      Dialogs.confirm({
         title: `Migrate ${selections.length} item${selections.length === 1 ? '' : 's'} to MAS?`,
         subtitle: 'Warehouse color/size are preserved; all other product fields are refetched from MAS. This cannot be undone.'
      }, async () => {
         await SavingUI.process(async () => {
            const {data} = await migrate({
               variables: {selections, sourcePreviewRunId: run?._id}
            });
            const newRunId = data?.BrandItemsMigrateToMasterDataVariant?._id;
            if (newRunId) {
               navigate(MasMigrationRunPagePath(newRunId));
            }
         }, 'Migration started', 'Starting migration, please wait');
      });
   };

   const retryableIds = items
      .filter(item => item.status === ItemStatus.Stale || item.status === ItemStatus.Failed)
      .map(item => item.brandItemId);

   const onRePreview = () => {
      if (retryableIds.length === 0) {
         return;
      }
      Dialogs.confirm({
         title: `Re-preview ${retryableIds.length} item${retryableIds.length === 1 ? '' : 's'}?`,
         subtitle: 'Starts a fresh preview run for the stale/failed items so they can be re-matched against MAS.'
      }, async () => {
         await SavingUI.process(async () => {
            const {data} = await rePreview({variables: {brandItemIds: retryableIds}});
            const newRunId = data?.BrandItemsPreviewMasterDataVariantMigration?._id;
            if (newRunId) {
               navigate(MasMigrationRunPagePath(newRunId));
            }
         }, 'Preview started', 'Starting preview, please wait');
      });
   };

   const counters: { label: string; value: number; color: ChipColor }[] = run
      ? (isPreview
         ? [
            {label: 'Auto', value: run.matchedCount, color: 'success'},
            {label: 'Review', value: run.reviewCount, color: 'warning'},
            {label: 'No match', value: run.noMatchCount, color: 'default'},
            {label: 'Stale', value: run.staleCount, color: 'warning'},
            {label: 'Failed', value: run.failedCount, color: 'error'},
         ]
         : [
            {label: 'Migrated', value: run.migratedCount, color: 'success'},
            {label: 'Stale', value: run.staleCount, color: 'warning'},
            {label: 'Failed', value: run.failedCount, color: 'error'},
         ])
      : [];

   const title = run
      ? `MAS migration · ${isPreview ? 'Preview' : 'Migration'}`
      : 'MAS migration';

   return <Page>
      <PageHeader title={title} onBack>
         {isPreview && <Tooltip title={terminal ? '' : 'Available once the preview finishes processing.'}>
            <span>
               <Button variant="contained" disabled={!terminal || selectedCount === 0} onClick={onMigrate}>
                  Migrate {selectedCount} selected
               </Button>
            </span>
         </Tooltip>}
         {!isPreview && retryableIds.length > 0 &&
            <Button variant="outlined" onClick={onRePreview}>Re-preview stale/failed</Button>}
      </PageHeader>
      <PageContent>
         {!run && <LinearProgress/>}
         {run && <>
            {run.status === RunStatus.Failed &&
               <Alert severity="error" sx={{mb: 2}}>This run failed. Review the rows below for details.</Alert>}

            <RunSummary counters={counters} processedCount={run.processedCount}
                        requestedCount={run.requestedCount} active={!terminal}/>

            {isPreview && <BodyText type="body2" sx={{color: 'text.secondary', mb: 1}}>
               Only <strong>Auto</strong> rows can be migrated here; <strong>Review</strong> rows are handled manually.
               Warehouse color/size are preserved — all other fields are refetched from MAS on migrate.
               Comparison shows current → MAS candidate (green = matches, red = differs).
            </BodyText>}

            {items.length === 0 && !terminal &&
               <BodyText type="body2" sx={{color: 'text.secondary'}}>Processing… rows appear as items complete.</BodyText>}

            {items.length > 0 && <Table size="small" sx={{'& td, & th': {verticalAlign: 'top'}}}>
               <TableHead>
                  <TableRow>
                     {isPreview && <TableCell padding="checkbox">
                        <Checkbox size="small" disabled={selectableAutoIds.length === 0}
                                  checked={allAutoSelected}
                                  indeterminate={selectedCount > 0 && !allAutoSelected}
                                  onChange={toggleAll}/>
                     </TableCell>}
                     <TableCell>Status</TableCell>
                     <TableCell>Conf.</TableCell>
                     <TableCell>Current → MAS candidate</TableCell>
                     <TableCell>Signals</TableCell>
                     <TableCell>Reasons</TableCell>
                     <TableCell>Item</TableCell>
                  </TableRow>
               </TableHead>
               <TableBody>
                  {items.map(item => {
                     const selectable = item.status === ItemStatus.Auto && !!item.proposedMasterDataVariantId;
                     return <TableRow key={item._id} hover>
                        {isPreview && <TableCell padding="checkbox">
                           <Tooltip title={selectable ? '' : 'Only Auto matches are migratable here.'}>
                              <span>
                                 <Checkbox size="small" disabled={!selectable}
                                           checked={selected.has(item._id)}
                                           onChange={() => toggleRow(item._id)}/>
                              </span>
                           </Tooltip>
                        </TableCell>}
                        <TableCell>
                           {statusChip(item)}
                           {item.error && <Tooltip title={item.error}>
                              <Box sx={{color: 'error.main', fontSize: '11px', mt: 0.5, maxWidth: 160, cursor: 'help'}}>
                                 {item.error}
                              </Box>
                           </Tooltip>}
                        </TableCell>
                        <TableCell>{item.confidence != null ? Math.round(item.confidence) : '—'}</TableCell>
                        <TableCell><FieldComparison item={item}/></TableCell>
                        <TableCell sx={{maxWidth: 280}}><SignalChips signals={item.signals}/></TableCell>
                        <TableCell sx={{maxWidth: 220, fontSize: '12px', color: 'text.secondary'}}>
                           {(item.reasons || []).join('; ') || '—'}
                        </TableCell>
                        <TableCell>
                           {item.brandHouseId
                              ? <Link icon={<OpenInNewIcon fontSize="inherit"/>} onClick={() => openBrandItem(item)}>Open</Link>
                              : <BodyText type="body2" sx={{color: 'text.secondary'}}>{String(item.brandItemId)}</BodyText>}
                        </TableCell>
                     </TableRow>;
                  })}
               </TableBody>
            </Table>}
         </>}
      </PageContent>
   </Page>;
};
